/**
 * Utilities for managing pending checkout state with automatic expiration.
 *
 * When users attempt to checkout without being authenticated, we store their
 * intent in localStorage. This utility enforces a 2-minute timeout to prevent
 * indefinitely showing checkout-related UI.
 */

import { createLogger } from './logger';

const PENDING_CHECKOUT_KEY = 'pending_checkout_price_id';
const PENDING_CHECKOUT_TIMESTAMP_KEY = 'pending_checkout_timestamp';

// 2 minutes in milliseconds
export const CHECKOUT_TIMEOUT_MS = 2 * 60 * 1000;

const checkoutLogger = createLogger('PendingCheckout', { flag: 'CACHE' });

/**
 * Store a pending checkout intent with the current timestamp.
 * @param priceId - Stripe price ID for the subscription plan
 * @returns true if successful, false if localStorage is unavailable or quota exceeded
 */
export const setPendingCheckout = (priceId: string): boolean => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(PENDING_CHECKOUT_KEY, priceId);
    localStorage.setItem(PENDING_CHECKOUT_TIMESTAMP_KEY, timestamp.toString());
    return true;
  } catch (error) {
    // Handle QuotaExceededError or other localStorage errors
    if (error instanceof Error) {
      if (checkoutLogger.isEnabled()) {
        checkoutLogger.warn('Failed to store pending checkout', {
          message: error.message,
        });
      }
    }
    return false;
  }
};

/**
 * Retrieve the pending checkout price ID if it hasn't expired.
 * Automatically cleans up expired entries.
 * @returns The price ID if valid and not expired, otherwise null
 */
export const getPendingCheckout = (): string | null => {
  try {
    const priceId = localStorage.getItem(PENDING_CHECKOUT_KEY);
    const timestampStr = localStorage.getItem(PENDING_CHECKOUT_TIMESTAMP_KEY);

    // No pending checkout
    if (!priceId || !timestampStr) {
      clearPendingCheckout();
      return null;
    }

    const timestamp = parseInt(timestampStr, 10);
    const now = Date.now();
    const elapsed = now - timestamp;

    // Check if expired (older than CHECKOUT_TIMEOUT_MS)
    if (elapsed > CHECKOUT_TIMEOUT_MS) {
      clearPendingCheckout();
      return null;
    }

    // Valid and not expired
    return priceId;
  } catch (error) {
    // Handle localStorage access errors gracefully
    if (error instanceof Error) {
      if (checkoutLogger.isEnabled()) {
        checkoutLogger.warn('Failed to retrieve pending checkout', {
          message: error.message,
        });
      }
    }
    return null;
  }
};

/**
 * Clear all pending checkout data from localStorage.
 */
export const clearPendingCheckout = (): void => {
  try {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
    localStorage.removeItem(PENDING_CHECKOUT_TIMESTAMP_KEY);
  } catch (error) {
    // Fail silently if localStorage is unavailable
    if (error instanceof Error) {
      if (checkoutLogger.isEnabled()) {
        checkoutLogger.warn('Failed to clear pending checkout', {
          message: error.message,
        });
      }
    }
  }
};
